import { enterKey, hasValue } from "@repo/helpers";
import { FC, MouseEventHandler, useMemo } from "react";
import ReactHtmlParser from "react-html-parser";

import style from "../style.module.scss";

//Regex to find clickable items: #= {something} #
const regexMatch = /(.*?)(#= ?([:\w. [\]]+) ?#)/g;
//Replace c# endlines with js endlines
const newLine = "<br />";

const noClickParents = ["settings", "representative", "user", "onboardingRequirements"];

const ClickableTemplate: FC<Props> = ({ template, values, properties, html, placeholder = "", onClick }) => {
	const baseTemplateString = useMemo(() => template.replace(/\r\n/g, newLine), [template]);

	const innerContent = useMemo(() => {
		const matches = baseTemplateString.matchAll(regexMatch);
		let templateString = baseTemplateString;
		let currentMatch = matches.next();
		let counter = 0;
		const innerContent = [] as (JSX.Element | string)[];
		while (!currentMatch.done) {
			const value = values[counter];
			templateString = templateString.replace(regexMatch, "");
			const currentValue = currentMatch.value[1] as string;
			currentValue.split(newLine).forEach((string, index, array) => {
				innerContent.push(...parseHtmlText(string));
				if (index !== array.length - 1) {
					innerContent.push(<br key={`lineBreak1-${counter}`} />);
				}
			});
			const property = properties[counter];
			if (!hasValue(value) || noClickParents.some((p) => property.startsWith(p))) {
				innerContent.push(
					<span key={`${property}-${counter}`} data-field={property}>
						{value ?? placeholder}
					</span>,
				);
			} else {
				innerContent.push(
					<span
						key={`${property}-${counter}`}
						data-field={property}
						className={style.summarySelection}
						tabIndex={1}
						onClick={onClick}
						onKeyDown={enterKey(onClick)}
					>
						{value}
					</span>,
				);
			}

			counter++;
			currentMatch = matches.next();
		}
		const linesOfTemplate = templateString.split(newLine);
		if (linesOfTemplate.length) {
			for (const line of linesOfTemplate) {
				innerContent.push(...parseHtmlText(line));
				innerContent.push(<br key={`lineBreak2-${counter}`} />);
			}
			innerContent.pop(); //Remove last newline
		}
		return innerContent;
	}, [baseTemplateString, values, properties, onClick]);

	return <span className={style.displayGroup}>{innerContent}</span>;

	function parseHtmlText(text: string) {
		return html ? ReactHtmlParser(text) : [text];
	}
};

export default ClickableTemplate;

interface Props {
	template: string;
	values: any[];
	properties: string[];
	html?: boolean;
	placeholder?: string;
	onClick: MouseEventHandler;
}
